import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenu} from '@Components/contextual-menu';
import {noop} from '@Utils/general.util';
import {TranscriptCustomizeOptionsContent} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-customize-styles-panel/components/transcript-customize-options-content/transcript-customize-options-content';
import {TranscriptCustomizeSubpanelContent} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-customize-styles-panel/components/transcript-customize-subpanel-content/transcript-customize-subpanel-content';
import type {TranscriptCustomizePanelProps} from './transcript-customize-styles-panel.types';
import {TranscriptContextualMenuSubPanel} from './transcript-customize-styles-panel.types';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {useGetKeyFromSubtitlePropertiesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitlePropertiesForTranscriptItem';

export function TranscriptCustomizeStylesPanel({onBack = noop}: TranscriptCustomizePanelProps): ReactElement<any> {
  const [subPanel, setSubPanel] = useState(TranscriptContextualMenuSubPanel.NONE);

  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in PosterEditingTranscriptOptions');
  }

  const animationStyle = useGetKeyFromSubtitlePropertiesForTranscriptItem('animationStyle');
  if (!animationStyle) {
    throw new Error('Transcript item does not have animationStyle property in its subtitle objects');
  }

  const isSubPanelOpened = (): boolean => {
    return subPanel !== TranscriptContextualMenuSubPanel.NONE;
  };

  const getSubPanelContent = (): ReactElement<any> => {
    return (
      <TranscriptCustomizeSubpanelContent
        subPanel={subPanel}
        onBack={() => {
          setSubPanel(TranscriptContextualMenuSubPanel.NONE);
        }}
      />
    );
  };

  const getCustomizeOptionsContent = (): ReactElement<any> => {
    return <TranscriptCustomizeOptionsContent setSubPanel={setSubPanel} />;
  };

  const getHeaderText = (): string => {
    switch (subPanel) {
      case TranscriptContextualMenuSubPanel.NONE:
        return window.i18next.t('pmwjs_customize');
      case TranscriptContextualMenuSubPanel.TRANSCRIPT_BACKGROUND_COLOR:
        return window.i18next.t('pmwjs_background_color');
      case TranscriptContextualMenuSubPanel.SENTENCE_TEXT_COLOR:
        return window.i18next.t('pmwjs_sentence_text_color');
      case TranscriptContextualMenuSubPanel.ACTIVE_WORD_TEXT_COLOR:
        return animationStyle === SubtitleTemplateType.PROGRESS ? window.i18next.t('pmwjs_highlight_words_text_color') : window.i18next.t('pmwjs_highlight_word_text_color');
      case TranscriptContextualMenuSubPanel.SENTENCE_TEXT_OUTLINE_COLOR:
        return window.i18next.t('pmwjs_sentence_outline_color');
      case TranscriptContextualMenuSubPanel.ACTIVE_WORD_TEXT_OUTLINE_COLOR:
        return animationStyle === SubtitleTemplateType.PROGRESS ? window.i18next.t('pmwjs_highlight_words_outline_color') : window.i18next.t('pmwjs_highlight_word_outline_color');
      case TranscriptContextualMenuSubPanel.TRANSCRIPT_SHADOW_COLOR:
        return window.i18next.t('pmwjs_shadow_color');
      default:
        throw new Error(`Unhandled sub-panel ${subPanel} for transcript customization`);
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (subPanel === TranscriptContextualMenuSubPanel.FONT) {
      return null;
    }

    return (
      <ContextualMenuHeader id="transcript-customize-panel">
        <ContextualMenuTextHeader
          showBackButton
          onBackButton={() => {
            if (subPanel === TranscriptContextualMenuSubPanel.NONE) {
              onBack();
            } else {
              setSubPanel(TranscriptContextualMenuSubPanel.NONE);
            }
          }}
          text={getHeaderText()}
        />
      </ContextualMenuHeader>
    );
  };

  const getContent = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getSubPanelContent();
    }

    return getCustomizeOptionsContent();
  };

  return (
    <ContextualMenu
      contentClassName={subPanel === TranscriptContextualMenuSubPanel.FONT ? 'spacing-p-0' : ''}
      includeLeftRightPadding={subPanel === TranscriptContextualMenuSubPanel.NONE}
      id="transcript-customize-styles-options"
      header={getHeader()}
    >
      {getContent()}
    </ContextualMenu>
  );
}
